/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pixelService } from '../services';
import type { ReusablePixel } from '../types';

export const PIXELS_KEY = 'pixels';

export function usePixels() {
  return useQuery({
    queryKey: [PIXELS_KEY],
    queryFn: () => pixelService.listPixels(),
  });
}

export function useCreatePixel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pixel: Partial<ReusablePixel>) => pixelService.createPixel(pixel),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PIXELS_KEY] }),
  });
}

export function useUpdatePixel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, pixel }: { id: string; pixel: Partial<ReusablePixel> }) => pixelService.updatePixel(id, pixel),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PIXELS_KEY] }),
  });
}

export function useDeletePixel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => pixelService.deletePixel(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PIXELS_KEY] }),
  });
}

export function useTogglePixel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => pixelService.togglePixel(id, enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PIXELS_KEY] }),
  });
}

export function useSetPixelVisibility() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, visibility }: { id: string; visibility: ReusablePixel['visibility'] }) =>
      pixelService.setVisibility(id, visibility),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [PIXELS_KEY] }),
  });
}
